<script lang="ts">
  import type { ContentItem } from '$lib/types/components';
  import type { HTMLBaseAttributes } from 'svelte/elements';
  import { twMerge } from 'tailwind-merge';

  type $$Props = ContentItem & {
    bodyClasses?: string;
    imgWrapperClasses?: string;
  };

  const { imgSrc, imgAlt, href, nonLazyImg, role, bodyClasses, imgWrapperClasses } =
    $$props as $$Props;

  export let variant: $$Props['variant'] = 'md';
  export let tag: $$Props['tag'] = href ? 'a' : 'div';
  export let scheme: $$Props['scheme'] = 'light';

  const classes = {
    sizes: {
      lg: 'md:gap-x-6 lg:gap-x-8',
      md: 'md:gap-x-5 lg:gap-x-6'
    },
    imgSize: {
      lg: 'md:w-72 md:rounded-lg',
      md: 'md:w-28 lg:w-40 md:rounded-md'
    }
  };

  const conditionalAttrs: HTMLBaseAttributes = {};

  if (tag === 'a' && href !== undefined) {
    conditionalAttrs.href = href; // Only include href if the tag is an anchor
  }
</script>

<svelte:element
  this={tag}
  class={twMerge(
    'group flex items-center gap-x-4 overflow-hidden',
    scheme === 'dark' && 'bg-blue-950 text-white',
    variant && classes.sizes[variant],
    $$restProps.class
  )}
  {...conditionalAttrs}
  {role}
  on:click
  on:focusin
  on:focusout
  on:mouseenter
  on:mouseleave
>
  {#if imgSrc}
    <div
      class={twMerge(
        'relative aspect-[3/2] w-24 shrink-0 self-start overflow-hidden rounded-sm',
        variant && classes.imgSize[variant],
        imgWrapperClasses
      )}
    >
      <img
        src={imgSrc}
        alt={imgAlt}
        class="h-full w-full object-cover"
        {...!nonLazyImg && { loading: 'lazy' }}
      />

      {#if href}
        <div
          class={twMerge(
            'absolute inset-0 z-10 bg-white opacity-0 transition-all duration-300 group-hover:opacity-30'
          )}
        />
      {/if}
    </div>
  {:else}
    <!-- Placeholder when no image is provided -->
    <div
      class={twMerge(
        'relative flex aspect-[3/2] w-24 shrink-0 items-center justify-center self-start overflow-hidden rounded-sm bg-gray-200',
        variant && classes.imgSize[variant],
        imgWrapperClasses
      )}
    >
      <!-- You can use an icon or simple text as placeholder -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-8 w-8 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
      {#if href}
        <div
          class={twMerge(
            'absolute inset-0 z-10 bg-white opacity-0 transition-all duration-300 group-hover:opacity-30'
          )}
        />
      {/if}
    </div>
  {/if}
  <div class={twMerge(bodyClasses)}>
    <slot />
  </div>
</svelte:element>

<!--
@component
Content list item with image on left and info on right.
## Props
@prop tag: string = $$restProps.href ? 'a' : 'div';
@prop size: 'lg' | 'sm' = 'sm';
@prop role: string | undefined = undefined;
@prop scheme: ColorScheme = 'light';
@prop bodyClasses: string | null = null;
@prop imgSrc: string | null = null;
@prop imgAlt: string | null = null;
@prop imgWrapperClasses: string | null = null;
-->
